import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AtAGlanceNotification,
  PortfolioAverageTimeCycleRequestBody,
  PortfolioKpiResponse,
  PortfolioMilestone,
  PortfolioMilestonesResponse,
  Study,
  StudyMilestones,
  TimeCycleChart
} from '@models';
import { CommonFunctionsService, UserDataService } from '@services';
import { PortfolioMilestoneMapper } from '@utils';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StudyService {
  private http: HttpClient = inject(HttpClient);
  private userDataService: UserDataService = inject(UserDataService);
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);
  private params: HttpParams = new HttpParams();

  private clientNamesList: string[] = [];

  // Caching mechanism for studies portfolio
  private cachedStudiesPortfolio$ = new Map<string, Observable<Study[]>>();
  private cachedPortfolioMilestones$ = new Map<string, Observable<PortfolioMilestone[]>>();
  private cachedAtAGlanceNotifications$ = new Map<string, Observable<AtAGlanceNotification[]>>();
  private studiesSubject = new BehaviorSubject<Study[] | null>(null);
  private currentCacheKey: string | null = null;

  constructor() {
    const clientNames = this.userDataService.getParentClientNames();
    const orgIds = this.userDataService.getUserOrgIds();
    const username = this.userDataService.getUsername();

    this.clientNamesList = clientNames || [];

    this.params = this.params.set('org_ids', orgIds?.join(',') ?? '');
    this.params = this.params.set('user_id', username ?? '');
  }

  getPortfolioKpi(
    studyList: string[],
    parentClientNames: string[]
  ): Observable<PortfolioKpiResponse> {
    const BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
    const GET_PORTFOLIO_KPI_URL = `${BASE_URL}/v1/studies/portfolio/kpis`;

    const params = this.params
      .set('study_sk_list', studyList.join(','))
      .set('parent_client_names', JSON.stringify(parentClientNames));

    return this.http.get<PortfolioKpiResponse>(GET_PORTFOLIO_KPI_URL, {
      withCredentials: true,
      params: params
    });
  }

  getPortfolioAverageTimeCycle(
    studySkList: string[] | null,
    parentClientNames: string[]
  ): Observable<TimeCycleChart> {
    const BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
    const GET_PORTFOLIO_AVERAGE_TIME_CYCLE_URL = `${BASE_URL}/v1/chart/time-cycle/portfolio`;
    const orgIds = (this.userDataService.getUserOrgIds() || [])
      .map((orgId) => Number(orgId))
      .filter((orgId) => Number.isInteger(orgId));

    const requestBody: PortfolioAverageTimeCycleRequestBody = {
      parent_client_names: parentClientNames,
      org_ids: orgIds,
      study_sk_list: studySkList
    };

    return this.http.post<TimeCycleChart>(GET_PORTFOLIO_AVERAGE_TIME_CYCLE_URL, requestBody, {
      withCredentials: true
    });
  }

  getMilestones(studySk: string): Observable<StudyMilestones> {
    const BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
    const GET_STUDY_MILESTONES_URL = `${BASE_URL}/v1/studies/${studySk}/milestones`;
    return this.commonFunctionsService
      .getRequestCommonParams(['parent_client_names', 'org_ids'])
      .pipe(
        switchMap((params) =>
          this.http.get<StudyMilestones>(GET_STUDY_MILESTONES_URL, {
            withCredentials: true,
            params: params
          })
        )
      );
  }

  getPortfolioMilestones(parentClientNames: string[]): Observable<PortfolioMilestone[]> {
    const cacheKey = this.getParentClientNamesCacheKey(parentClientNames);

    if (!this.cachedPortfolioMilestones$.has(cacheKey)) {
      const BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
      const GET_PORTFOLIO_MILESTONES_URL = `${BASE_URL}/v1/studies/portfolio/milestones`;
      const params = this.params
        .set('parent_client_names', JSON.stringify(parentClientNames))
        .delete('user_id');

      const milestonesObservable$ = this.http
        .get<PortfolioMilestonesResponse>(GET_PORTFOLIO_MILESTONES_URL, {
          withCredentials: true,
          params: params
        })
        .pipe(
          map((response: PortfolioMilestonesResponse) => {
            const mapper = new PortfolioMilestoneMapper(this.commonFunctionsService);
            return mapper.mapFromJsonToModel(response);
          }),
          shareReplay(1)
        );

      this.cachedPortfolioMilestones$.set(cacheKey, milestonesObservable$);
    }
    return this.cachedPortfolioMilestones$.get(cacheKey)!;
  }

  getClientNamesList(): string[] {
    const latestClientNames = this.userDataService.getParentClientNames();
    if (latestClientNames) {
      this.clientNamesList = latestClientNames;
    }
    return this.clientNamesList;
  }

  getParentClientNames(): string[] {
    return this.userDataService.getParentClientNames() || [];
  }

  getStudiesPortfolio(parentClientNames: string[]): Observable<Study[]> {
    const cacheKey = this.getParentClientNamesCacheKey(parentClientNames);

    if (!this.cachedStudiesPortfolio$.has(cacheKey)) {
      const BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
      const GET_STUDIES_PORTFOLIO_URL = `${BASE_URL}/v1/studies/portfolio`;
      const params = this.params.set('parent_client_names', JSON.stringify(parentClientNames));
      const studiesObservable$ = this.http
        .get<any[]>(GET_STUDIES_PORTFOLIO_URL, {
          withCredentials: true,
          params: params
        })
        .pipe(
          map((studies: any[]) =>
            studies.map((study) => {
              const normalizedStudy = {
                ...study,
                studySk: study.studySk?.toString()
              };

              if (normalizedStudy.milestoneDates == null) {
                delete normalizedStudy.milestoneDates;
              }

              return normalizedStudy;
            })
          ),
          tap((studies: Study[]) => {
            if (this.currentCacheKey === cacheKey) {
              this.studiesSubject.next(studies);
            }
          }),
          shareReplay(1)
        );

      this.cachedStudiesPortfolio$.set(cacheKey, studiesObservable$);
    }

    if (this.currentCacheKey !== cacheKey) {
      this.currentCacheKey = cacheKey;
      const cachedObservable = this.cachedStudiesPortfolio$.get(cacheKey);
      if (cachedObservable) {
        cachedObservable.pipe(tap((studies) => this.studiesSubject.next(studies))).subscribe();
      }
    }

    return this.cachedStudiesPortfolio$.get(cacheKey)!;
  }

  getAtAGlanceNotifications(parentClientNames: string[]): Observable<AtAGlanceNotification[]> {
    const cacheKey = this.getParentClientNamesCacheKey(parentClientNames);

    if (!this.cachedAtAGlanceNotifications$.has(cacheKey)) {
      const BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
      const GET_AT_A_GLANCE_NOTIFICATIONS_URL = `${BASE_URL}/v1/studies/portfolio/notifications/at-a-glance`;
      const params = this.params.set('parent_client_names', JSON.stringify(parentClientNames));

      const notificationsObservable$ = this.http
        .get<AtAGlanceNotification[]>(GET_AT_A_GLANCE_NOTIFICATIONS_URL, {
          withCredentials: true,
          params: params
        })
        .pipe(shareReplay(1));

      this.cachedAtAGlanceNotifications$.set(cacheKey, notificationsObservable$);
    }

    return this.cachedAtAGlanceNotifications$.get(cacheKey)!;
  }

  getCachedStudiesPortfolio(): BehaviorSubject<Study[] | null> {
    return this.studiesSubject;
  }

  clearStudiesPortfolioCache(parentClientNames?: string[]): void {
    if (parentClientNames) {
      const cacheKey = this.getParentClientNamesCacheKey(parentClientNames);
      this.cachedStudiesPortfolio$.delete(cacheKey);
      this.cachedPortfolioMilestones$.delete(cacheKey);
      this.cachedAtAGlanceNotifications$.delete(cacheKey);

      if (this.currentCacheKey === cacheKey) {
        this.currentCacheKey = null;
        this.studiesSubject.next(null);
      }
    } else {
      this.cachedStudiesPortfolio$.clear();
      this.cachedPortfolioMilestones$.clear();
      this.cachedAtAGlanceNotifications$.clear();
      this.currentCacheKey = null;
      this.studiesSubject.next(null);
    }
  }

  private getParentClientNamesCacheKey(parentClientNames: string[]): string {
    const normalizedParentClientNames = Array.from(
      new Set(
        (parentClientNames ?? []).map((name) => name.trim()).filter((name) => name.length > 0)
      )
    ).sort((a, b) => a.localeCompare(b));

    return JSON.stringify(normalizedParentClientNames);
  }
}
